import { useState, useEffect, useRef, useCallback } from 'react'

const BRIDGE_URL = 'ws://127.0.0.1:8765'

export function useBridge() {
  const [state,       setState]       = useState('disconnected') // disconnected | connecting | connected
  const [bleState,    setBleState]    = useState(null)
  const [devices,     setDevices]     = useState([])
  const [connected,   setConnected]   = useState(false)
  const [deviceName,  setDeviceName]  = useState(null)
  const [recording,   setRecording]   = useState(false)
  const [recordStart, setRecordStart] = useState(null)
  const [sampleCount, setSampleCount] = useState(0)
  const [sessions,    setSessions]    = useState([])
  const [logs,        setLogs]        = useState([])
  const [lastSamples, setLastSamples] = useState(null) // { samples, startTime, endTime }
  const [error,       setError]       = useState(null)

  const wsRef = useRef(null)
  const reconnTimer = useRef(null)
  const intentionalClose = useRef(false)

  const addLog = msg => setLogs(prev => [...prev.slice(-49), `${new Date().toLocaleTimeString('es-AR')} ${msg}`])

  const send = useCallback(obj => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(obj))
    }
  }, [])

  const connect = useCallback(() => {
    if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close() }
    intentionalClose.current = false
    setState('connecting')
    const ws = new WebSocket(BRIDGE_URL)
    wsRef.current = ws

    ws.onopen = () => {
      setState('connected')
      setError(null)
      ws.send(JSON.stringify({ cmd: 'getStatus' }))
    }

    ws.onmessage = ({ data }) => {
      let msg
      try { msg = JSON.parse(data) } catch { return }

      if (msg.type === 'status') {
        setConnected(msg.connected)
        setDeviceName(msg.deviceName)
        setRecording(msg.recording)
        setRecordStart(msg.recordStart)
        setSampleCount(msg.sampleCount ?? 0)
      } else if (msg.type === 'bleState') {
        setBleState(msg.state)
      } else if (msg.type === 'devices') {
        setDevices(msg.devices)
      } else if (msg.type === 'log') {
        addLog(msg.msg)
      } else if (msg.type === 'error') {
        setError(msg.msg)
        addLog('ERROR: ' + msg.msg)
      } else if (msg.type === 'sessions') {
        setSessions(msg.sessions)
      } else if (msg.type === 'recordingDone') {
        setLastSamples({ samples: msg.samples, startTime: msg.startTime, endTime: msg.endTime })
      } else if (msg.type === 'sessionData') {
        setLastSamples({ samples: msg.samples, startTime: msg.startTime, endTime: Date.now() })
      } else if (msg.type === 'hrData') {
        setSampleCount(msg.count)
      }
    }

    ws.onclose = () => {
      setState('disconnected')
      setConnected(false)
      clearTimeout(reconnTimer.current)
    }

    ws.onerror = () => {
      setState('disconnected')
      setConnected(false)
      clearTimeout(reconnTimer.current)
    }
  }, [])

  useEffect(() => {
    // No longer connects on mount
    return () => {
      clearTimeout(reconnTimer.current)
      if (wsRef.current) {
        intentionalClose.current = true
        wsRef.current.onclose = null
        wsRef.current.close()
      }
    }
  }, [])

  const scan = useCallback(() => { send({ cmd: 'scan' }); setDevices([]) }, [send])
  const connectDevice = useCallback(deviceId => send({ cmd: 'connect', deviceId }), [send])
  const disconnect = useCallback(() => send({ cmd: 'disconnect' }), [send])
  const startRecording = useCallback(() => send({ cmd: 'startRecording' }), [send])
  const stopRecording = useCallback(() => send({ cmd: 'stopRecording' }), [send])
  const listSessions = useCallback(() => send({ cmd: 'listSessions' }), [send])
  const downloadSession = useCallback((path, dateStr, timeStr) => send({ cmd: 'downloadSession', path, dateStr, timeStr }), [send])
  const clearLastSamples = useCallback(() => setLastSamples(null), [])

  return {
    // Bridge connection state
    bridgeState: state,          // 'disconnected' | 'connecting' | 'connected'
    bridgeAvailable: state === 'connected',
    connectBridge: connect,
    bleState,
    // Device
    devices, scan, connectDevice, disconnect,
    connected, deviceName,
    // Recording
    recording, recordStart, sampleCount,
    startRecording, stopRecording,
    // Sessions
    sessions, listSessions, downloadSession,
    // Results
    lastSamples, clearLastSamples,
    // Debug
    logs, error,
  }
}
